import { useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { RefreshCw, CheckCircle2, AlertTriangle, XCircle, MinusCircle, ExternalLink, Info } from 'lucide-react';
import { useSubsidieIntake, type IntakeTarget } from '@/hooks/useSubsidieIntake';
import { useSubsidieRegelingen } from '@/hooks/useSubsidieRegelingen';
import { useEvaluateSubsidies, evaluateAllSubsidies, type SubsidieMatch } from '@/hooks/useEvaluateSubsidies';

interface SubsidieDashboardProps {
  target: IntakeTarget;
}

const STATUS_CONFIG = {
  volledig: { icon: CheckCircle2, color: 'text-green-600', bg: 'bg-green-50', label: 'Komt in aanmerking', badgeVariant: 'default' as const },
  gedeeltelijk: { icon: AlertTriangle, color: 'text-amber-600', bg: 'bg-amber-50', label: 'Data incompleet', badgeVariant: 'secondary' as const },
  niet_voldaan: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-50', label: 'Niet voldaan', badgeVariant: 'destructive' as const },
  niet_relevant: { icon: MinusCircle, color: 'text-muted-foreground', bg: 'bg-muted', label: 'Niet van toepassing', badgeVariant: 'outline' as const },
};

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(amount);
}

export function SubsidieDashboard({ target }: SubsidieDashboardProps) {
  const { intake, intakeData, volledigheid } = useSubsidieIntake(target);
  const { regelingen, regels, bedragen, combinaties, isLoading: regelingenLoading } = useSubsidieRegelingen(2026);
  const { matches, isLoading: matchesLoading, evaluate, isEvaluating } = useEvaluateSubsidies(target);

  const handleEvaluate = async () => {
    if (!intake) return;
    const results = evaluateAllSubsidies(intakeData, regelingen, regels, bedragen);
    await evaluate({ intakeId: intake.id, results });
  };

  // Auto-evaluate when intake changes
  useEffect(() => {
    if (intake && regelingen.length > 0 && regels.length > 0) {
      handleEvaluate();
    }
  }, [intake?.updated_at, regelingen.length]);

  if (regelingenLoading || matchesLoading) {
    return <div className="flex items-center justify-center p-8 text-muted-foreground">Laden...</div>;
  }

  if (!intake) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          <p>Nog geen subsidie-intake ingevuld. Vul eerst het intake-formulier in.</p>
        </CardContent>
      </Card>
    );
  }

  // Build display data - either from persisted matches or compute on-the-fly
  const displayMatches = matches.length > 0
    ? matches
    : evaluateAllSubsidies(intakeData, regelingen, regels, bedragen).map(r => ({
        id: '',
        intake_id: intake.id,
        regeling_id: r.regeling.id,
        match_status: r.match_status,
        geschat_subsidie: r.regeling.type === 'subsidie' ? r.geschat_totaal : 0,
        geschat_fiscaal: r.regeling.type === 'fiscaal' ? r.geschat_totaal : 0,
        geschat_totaal: r.geschat_totaal,
        berekening: r.berekening,
        onvervulde_regels: r.onvervulde_regels,
        aanbevelingen: r.aanbevelingen,
        berekend_op: new Date().toISOString(),
        regeling: r.regeling,
      }));

  const totaalSubsidie = displayMatches
    .filter(m => m.match_status === 'volledig' || m.match_status === 'gedeeltelijk')
    .reduce((sum, m) => sum + (m.geschat_totaal ?? 0), 0);

  const sortedMatches = [...displayMatches].sort((a, b) => {
    const order = { volledig: 0, gedeeltelijk: 1, niet_voldaan: 2, niet_relevant: 3 };
    return (order[a.match_status] ?? 4) - (order[b.match_status] ?? 4);
  });

  return (
    <div className="space-y-4">
      {/* Summary bar */}
      <Card>
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Geschat totaal voordeel</p>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(totaalSubsidie)}</p>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <p className="text-xs text-muted-foreground mt-1 cursor-help underline decoration-dotted">
                      Schatting op basis van RVO-tarieven 2026
                    </p>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p>Bedragen zijn indicatief. Exacte subsidiebedragen worden bepaald door RVO bij beoordeling. Fiscale voordelen (EIA/MIA) zijn afhankelijk van uw fiscale situatie.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm text-muted-foreground">Intake volledigheid</p>
                <div className="flex items-center gap-2">
                  <Progress value={volledigheid} className="w-24 h-2" />
                  <span className="text-sm font-medium">{volledigheid}%</span>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={handleEvaluate} disabled={isEvaluating}>
                <RefreshCw className={`h-4 w-4 mr-2 ${isEvaluating ? 'animate-spin' : ''}`} />
                Herbereken
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Per-regeling cards */}
      <div className="grid gap-3">
        {sortedMatches.map(match => {
          const regeling = match.regeling ?? regelingen.find(r => r.id === match.regeling_id);
          if (!regeling) return null;

          const config = STATUS_CONFIG[match.match_status];
          const StatusIcon = config.icon;
          const berekening = (match.berekening ?? {}) as Record<string, number>;
          const onvervuld = (match.onvervulde_regels ?? []) as Array<{ beschrijving: string; foutmelding: string }>;
          const tips = (match.aanbevelingen ?? []) as string[];

          return (
            <Card key={match.regeling_id} className={`${config.bg} border`}>
              <CardContent className="py-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <StatusIcon className={`h-5 w-5 mt-0.5 ${config.color}`} />
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold">{regeling.korte_naam}</h3>
                        <Badge variant={config.badgeVariant} className="text-xs">
                          {config.label}
                        </Badge>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge variant="outline" className="text-xs cursor-help">
                                {regeling.type === 'subsidie' ? 'Subsidie' : 'Fiscaal'}
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent>
                              {regeling.type === 'subsidie'
                                ? 'Directe subsidie — wordt uitbetaald door RVO'
                                : 'Fiscaal voordeel — verlaging van belastbare winst (geen directe uitbetaling)'}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        {regeling.status !== 'open' && (
                          <Badge variant="secondary" className="text-xs capitalize">
                            {regeling.status === 'gepland' && regeling.aanvraag_open
                              ? `Opent ${new Date(regeling.aanvraag_open).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })}`
                              : regeling.status}
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">{regeling.samenvatting}</p>

                      {/* Breakdown */}
                      {Object.keys(berekening).length > 0 && (
                        <div className="mt-2 space-y-1">
                          {Object.entries(berekening).map(([label, amount]) => (
                            <div key={label} className="flex items-center gap-2 text-sm">
                              <span className="text-muted-foreground">{label}:</span>
                              <span className="font-medium">{formatCurrency(amount as number)}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Unmet rules */}
                      {onvervuld.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {onvervuld.map((r, i) => (
                            <div key={i} className="flex items-start gap-1 text-sm text-red-600">
                              <XCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                              <span>{r.foutmelding || r.beschrijving}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Tips */}
                      {tips.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {tips.map((tip, i) => (
                            <div key={i} className="flex items-start gap-1 text-sm text-amber-600">
                              <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                              <span>{tip}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Deadline with warning colors */}
                      {regeling.aanvraag_sluit && (() => {
                        const daysLeft = Math.ceil((new Date(regeling.aanvraag_sluit).getTime() - Date.now()) / 86400000);
                        const deadlineColor = daysLeft < 7 ? 'text-red-600 font-medium' : daysLeft < 30 ? 'text-amber-600' : 'text-muted-foreground';
                        const deadlineLabel = daysLeft < 0 ? 'Gesloten' : daysLeft < 7 ? `Nog ${daysLeft} dagen!` : daysLeft < 30 ? `Nog ${daysLeft} dagen` : '';
                        return (
                          <p className={`text-xs mt-2 ${deadlineColor}`}>
                            Deadline: {new Date(regeling.aanvraag_sluit).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })}
                            {deadlineLabel && ` — ${deadlineLabel}`}
                          </p>
                        );
                      })()}

                      {/* Budget warning for SPRILA battery */}
                      {regeling.code === 'SPRILA' && (
                        <p className="text-xs text-red-600 mt-1">
                          Batterijbudget (€18,5M) overtekend per 11 feb 2026
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="text-lg font-bold">{formatCurrency(match.geschat_totaal ?? 0)}</p>
                      <p className="text-xs text-muted-foreground">geschat</p>
                    </div>
                    {regeling.rvo_url && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <a href={regeling.rvo_url} target="_blank" rel="noopener noreferrer">
                              <ExternalLink className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                            </a>
                          </TooltipTrigger>
                          <TooltipContent>Bekijk op RVO.nl</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
